import React, { useEffect, useMemo, useRef, useState } from 'react';
import PageNavigation from './PageNavigation';
import type { PostModel } from '../lib/posts';
import { listMedia, type MediaItem } from '../lib/media';

type Props = {
  value: PostModel;
  slug: string;
  onChange: (next: PostModel & { draft?: boolean }, extras: { slug: string; draft: boolean }) => void;
};

const cls = {
  label: 'text-xs font-medium text-gray-600',
  input: 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500',
  textarea: 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 min-h-[90px] resize-y',
};

const PostSettings: React.FC<Props> = ({ value, slug, onChange }) => {
  const [local, setLocal] = useState<PostModel & { draft?: boolean }>(() => ({ ...value, draft: (value as any).draft ?? false }));
  const [localSlug, setLocalSlug] = useState<string>(slug);

  // Keep internal state in sync when parent value/slug changes
  useEffect(() => {
    setLocal({ ...value, draft: (value as any).draft ?? false });
  }, [value]);
  useEffect(() => {
    setLocalSlug(slug);
  }, [slug]);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [autoSlug, setAutoSlug] = useState<boolean>(true);

  useEffect(() => {
    setLocal(prev => {
      // Preserve current draft toggle if present unless value explicitly carries it
      const d = (value as any).draft ?? prev.draft ?? false;
      return { ...value, draft: d } as any;
    });
  }, [value]);

  // Only recompute autoSlug when the selected post changes (slug prop changes),
  // so typing in title doesn't flip the checkbox.
  const prevSlugRef = useRef<string>(slug);
  useEffect(() => {
    if (prevSlugRef.current !== slug) {
      prevSlugRef.current = slug;
      setLocalSlug(slug);
      try {
        const ideal = slugify((value?.title || ''));
        setAutoSlug(prev => prev || slug === '' || slug === ideal);
      } catch {}
    } else {
      // Keep localSlug aligned with prop without touching autoSlug state
      setLocalSlug(slug);
    }
  }, [slug]);

  const slugify = (s: string) => s.toLowerCase().trim().replace(/\s+/g,'-').replace(/[^a-z0-9\-]/g,'');

  function emit(next: PostModel & { draft?: boolean }, nextSlug = localSlug) {
    onChange(next, { slug: nextSlug, draft: !!next.draft });
  }

  function update<K extends keyof (PostModel & { draft?: boolean })>(key: K, v: (PostModel & { draft?: boolean })[K]) {
    setLocal(prev => {
      const next = { ...prev, [key]: v } as PostModel & { draft?: boolean };
      emit(next);
      return next;
    });
  }

  // If autoSlug is enabled, keep slug synced to title on any title change
  useEffect(() => {
    if (!autoSlug) return;
    const s = slugify((local.title || ''));
    if (s !== localSlug) {
      setLocalSlug(s);
      emit(local, s);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSlug, local.title]);

  const onPickFromGallery = async () => {
    setMediaOpen(true);
    setMediaLoading(true);
    try {
      const items = await listMedia();
      setMediaItems(items);
    } finally { setMediaLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={cls.label}>Title</label>
        <input
          className={cls.input}
          value={local.title}
          onChange={(e)=> {
            const t = e.target.value;
            setLocal(prev => {
              const next = { ...prev, title: t } as any;
              if (autoSlug) {
                const s = slugify(t);
                setLocalSlug(s);
                emit(next, s);
              } else {
                emit(next);
              }
              return next;
            });
          }}
        />
        <div className="mt-2">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50"
            title="Auto-assign title from content"
            onClick={() => {
              const blocks: any[] = Array.isArray((local as any).blocks) ? ((local as any).blocks as any[]) : [];
              const textOf = (b: any) => (b && typeof b.text === 'string') ? b.text.trim() : '';
              const byScore = (s: string) => s.replace(/\s+/g,' ').trim();
              let candidate = '';
              // Prefer Heading 1
              for (const b of blocks) { if (b?.type === 'heading1') { const t = textOf(b); if (t) { candidate = t; break; } } }
              // Else Heading 2
              if (!candidate) { for (const b of blocks) { if (b?.type === 'heading2') { const t = textOf(b); if (t) { candidate = t; break; } } } }
              // Else first sentence of the first paragraph with decent length
              if (!candidate) {
                const firstSentence = (txt: string): string => {
                  const m = txt.match(/(.{1,140}?)([\.\!\?…])(\s|$)/);
                  if (m) { return (m[1] + m[2]).trim(); }
                  return txt.slice(0, 140).trim();
                };
                for (const b of blocks) {
                  if (b?.type === 'paragraph') {
                    const t = textOf(b);
                    if (t && t.replace(/\s+/g,' ').trim().length >= 8) {
                      candidate = firstSentence(t);
                      break;
                    }
                  }
                }
              }
              // Fallback to any non-empty text
              if (!candidate) { for (const b of blocks) { const t = textOf(b); if (t) { candidate = t; break; } } }
              candidate = byScore(candidate).slice(0, 120);
              if (!candidate) return;
              // Apply just like typing in the Title field, honoring autoSlug
              setLocal(prev => {
                const next = { ...prev, title: candidate } as any;
                if (autoSlug) {
                  const s = slugify(candidate);
                  setLocalSlug(s);
                  emit(next, s);
                } else {
                  emit(next);
                }
                return next;
              });
            }}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18"/><path d="M5 12h14"/></svg>
            Auto-assign
          </button>
        </div>
      </div>
      <div>
        <label className={cls.label}>Comments</label>
        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50/40 p-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-gray-900">Allow comments</div>
            <div className="text-xs text-gray-600">Enable readers to comment on this post.</div>
          </div>
          <button
            type="button"
            onClick={()=> update('commentsEnabled' as any, !Boolean((local as any).commentsEnabled))}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 ${Boolean((local as any).commentsEnabled) ? 'bg-green-600' : 'bg-gray-300'}`}
            aria-pressed={Boolean((local as any).commentsEnabled)}
            aria-label="Toggle comments"
            title={Boolean((local as any).commentsEnabled) ? 'Disable comments' : 'Enable comments'}
          >
            <span className={`pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ${Boolean((local as any).commentsEnabled) ? 'translate-x-7' : 'translate-x-1'}`}></span>
          </button>
        </div>
      </div>
      <div>
        <label className={cls.label}>Page navigation style</label>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium ring-1 bg-purple-50 text-purple-800 ring-purple-200 capitalize">
            {((local as any).navStyle || 'classic')} style
          </div>
          <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={()=> setNavOpen(true)}>
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 8h10"/><path d="M7 12h6"/></svg>
            Choose navigation styles
          </button>
        </div>
        <div className="mt-1 text-xs text-gray-500">Controls how page navigation appears at the bottom of your post.</div>
      </div>
      <div>
        <label className={cls.label}>Slug (URL)</label>
        <div className="mt-1 flex items-center gap-2">
          <input
            className={cls.input}
            value={localSlug}
            onChange={(e)=> {
              const s = slugify(e.target.value);
              setAutoSlug(false);
              setLocalSlug(s);
              emit(local, s);
            }}
            placeholder="post-title-1"
          />
          <label className="inline-flex items-center gap-2 text-xs text-gray-700 select-none">
            <input
              type="checkbox"
              checked={autoSlug}
              onChange={(e)=> {
                const v = e.target.checked;
                setAutoSlug(v);
                if (v) {
                  const s = slugify(local.title || '');
                  setLocalSlug(s);
                  emit(local, s);
                }
              }}
            />
            Keep in sync with title
          </label>
        </div>
        <div className="mt-1 text-xs text-gray-500">This appears as /posts/<span className="font-mono">{localSlug || 'post-title-1'}</span>. {autoSlug ? 'Slug updates automatically from the title.' : 'You can type a custom slug. Toggle sync to regenerate from the title.'}</div>
      </div>
      <div>
        <label className={cls.label}>Excerpt</label>
        <textarea className={cls.textarea} value={local.excerpt || ''} onChange={(e)=> update('excerpt', e.target.value)} />
        <div className="mt-2">
          <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="7" width="18" height="14" rx="3" ry="3"/>
              <path d="M7 7V5a5 5 0 0 1 10 0v2"/>
              <circle cx="9" cy="14" r="1"/>
              <circle cx="15" cy="14" r="1"/>
              <path d="M9 18h6"/>
            </svg>
            Summarize with AI
          </button>
        </div>
      </div>
      <div>
        <label className={cls.label}>Cover image URL</label>
        <div className="mt-1 grid grid-cols-[1fr_auto] gap-2">
          <input className={cls.input} value={local.cover || ''} onChange={(e)=> update('cover', e.target.value)} placeholder="https://..." />
          <button type="button" onClick={onPickFromGallery} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
            Choose
          </button>
        </div>
        {local.cover && (
          <div className="mt-2">
            <img src={local.cover} alt="Cover preview" className="h-28 w-full object-cover rounded-lg border border-gray-200" />
          </div>
        )}

      {navOpen && (
        <div className="fixed inset-0 z-[81] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={()=> setNavOpen(false)} />
          <div className="relative z-10 w-[min(900px,95vw)] max-h-[85vh] overflow-auto bg-white rounded-2xl shadow-2xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="font-semibold text-gray-900">Choose navigation style</div>
              <button className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100" onClick={()=> setNavOpen(false)}>
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="grid gap-4">
              {(['classic','minimal','tabs'] as const).map((style) => (
                <button
                  key={style}
                  type="button"
                  onClick={()=> { update('navStyle' as any, style as any); setNavOpen(false); }}
                  className={`text-left rounded-xl border p-4 transition ${((local as any).navStyle || 'classic')===style ? 'border-purple-300 ring-2 ring-purple-200 bg-purple-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                  title={`Use ${style} style`}
                >
                  <div className="text-xs font-medium text-gray-700 mb-3 capitalize">{style}</div>
                  <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <PageNavigation
                      pages={[{title:'Page 1'},{title:'Page 2'},{title:'Page 3'}]}
                      currentIndex={1}
                      onSelect={()=>{}}
                      stylePreset={style}
                    />
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-end">
              <button className="inline-flex items-center rounded-md border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={()=> setNavOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={()=> update('draft', !local.draft)} className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium ring-1 ${local.draft ? 'bg-amber-100 text-amber-800 ring-amber-200' : 'bg-gray-100 text-gray-700 ring-gray-200'}`}>
          {local.draft ? (
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2h12a2 2 0 0 1 2 2v18l-8-4-8 4V4a2 2 0 0 1 2-2z"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2h12a2 2 0 0 1 2 2v18l-8-4-8 4V4a2 2 0 0 1 2-2z"/>
              <path d="M8 7h8"/>
            </svg>
          )}
          Save Draft
        </button>
      </div>

      {mediaOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={()=> setMediaOpen(false)} />
          <div className="relative z-10 w-[min(800px,95vw)] max-h-[80vh] overflow-auto bg-white rounded-2xl shadow-2xl border border-gray-200 p-4">
            <div className="font-semibold text-gray-900 mb-3">Choose cover image</div>
            {mediaLoading && <div className="text-sm text-gray-600">Loading…</div>}
            {!mediaLoading && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {mediaItems.map((m)=> (
                  <button key={m.name} className="group rounded-lg border border-gray-200 overflow-hidden hover:shadow" onClick={()=> { update('cover', m.url); setMediaOpen(false); }}>
                    <img src={m.url} alt={m.name} className="h-28 w-full object-cover" />
                    <div className="px-2 py-1 text-xs text-gray-600 truncate group-hover:text-gray-900">{m.name}</div>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4 flex items-center justify-end">
              <button className="inline-flex items-center rounded-md border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50" onClick={()=> setMediaOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostSettings;
